import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Coins, MessageSquareText } from "lucide-react";
import { STATUS, fmtBRL } from "@/lib/api";

const ConfirmBtn = ({ onClick, disabled, saving, isSuccess, label, testId, className = "" }) => (
  <Button onClick={onClick} disabled={disabled}
    className={`w-full h-12 text-lg ${isSuccess ? "bg-green-600 hover:bg-green-600 ring-2 ring-green-300" : className || "bg-orange-600 hover:bg-orange-700"}`}
    data-testid={testId}>
    {isSuccess ? <><CheckCircle2 className="w-5 h-5 mr-2" />Enviado!</> : saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Enviando…</> : label}
  </Button>
);

/** Iter 118: card do troco (dinheiro) — o entregador confirma em 1 toque, sem digitar. */
function TrocoCard({ prompt, setPrompt, onConfirm, lock, saving, isSuccess }) {
  const { valor, trocoPara, troco, trocoOk, need4dig } = prompt;
  const label = troco > 0 ? `Confirmei o troco de ${fmtBRL(troco)}` : `Recebi ${fmtBRL(valor)}`;
  const onTap = () => (need4dig ? setPrompt({ ...prompt, trocoOk: !trocoOk }) : onConfirm({ trocoOk: true }));
  return (
    <div className={`rounded-xl border p-3 ${trocoOk ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="mobile-troco-card" data-ok={trocoOk}>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div><div className="text-[10px] uppercase font-bold text-slate-500">Valor</div><div className="mono font-bold" data-testid="mobile-troco-valor">{fmtBRL(valor)}</div></div>
        <div><div className="text-[10px] uppercase font-bold text-slate-500">Cliente paga</div><div className="mono font-bold" data-testid="mobile-troco-para">{fmtBRL(trocoPara > 0 ? trocoPara : valor)}</div></div>
        <div><div className="text-[10px] uppercase font-bold text-slate-500">Troco</div><div className="mono font-extrabold text-orange-700 text-lg" data-testid="mobile-troco-troco">{troco > 0 ? fmtBRL(troco) : "sem troco"}</div></div>
      </div>
      {need4dig ? (
        <Button type="button" variant={trocoOk ? "default" : "outline"} onClick={onTap} disabled={lock}
          className={`w-full mt-3 h-11 ${trocoOk ? "bg-emerald-600 hover:bg-emerald-700" : "border-amber-400 text-amber-900 hover:bg-amber-100"}`} data-testid="mobile-troco-confirmar">
          {trocoOk ? <><CheckCircle2 className="w-5 h-5 mr-2" />{troco > 0 ? "Troco confirmado" : "Valor confirmado"}</> : <><Coins className="w-5 h-5 mr-2" />{label}</>}
        </Button>
      ) : (
        <div className="mt-3"><ConfirmBtn onClick={onTap} disabled={lock} saving={saving} isSuccess={isSuccess} label={<><Coins className="w-5 h-5 mr-2" />{label}</>} testId="mobile-troco-confirmar" className="bg-emerald-700 hover:bg-emerald-800" /></div>
      )}
    </div>
  );
}

/** Iter 118: observação opcional do entregador (recolhida por padrão para não atrasar). */
function ObsField({ prompt, setPrompt, lock }) {
  if (prompt.obsOpen || prompt.obs) {
    return <Textarea value={prompt.obs || ""} onChange={(e) => setPrompt({ ...prompt, obs: e.target.value })} disabled={lock} rows={2} maxLength={500}
      placeholder="Observação (opcional) — ex.: deixei com o porteiro" data-testid="mobile-prompt-obs" autoFocus />;
  }
  return (
    <button type="button" onClick={() => setPrompt({ ...prompt, obsOpen: true })} disabled={lock} className="text-sm text-sky-700 inline-flex items-center gap-1" data-testid="mobile-obs-toggle">
      <MessageSquareText className="w-4 h-4" />Adicionar observação (opcional)
    </button>
  );
}

/**
 * Dialog de confirmação do status.
 * - concluir (Iter 118): código 4 dígitos (se exigido) + card do troco em 1 toque (dinheiro) + observação opcional.
 * - motivo: cancelar/remarcar.
 * Props: prompt, setPrompt, onConfirm(overrides?), saving, flowBusy (green check "Enviado!" por 1s).
 */
export function MobileStatusPrompt({ prompt, setPrompt, onConfirm, saving = false, flowBusy = null }) {
  const isSuccess = flowBusy?.phase === "success" && flowBusy?.entregaId === prompt?.entrega?.id;
  const lock = saving || isSuccess;
  const isConcluir = prompt?.field === "concluir";
  const code = String(prompt?.value || "").replace(/\D/g, "");
  const canConfirm = !isConcluir || ((!prompt.need4dig || code.length === 4) && (!prompt.needTroco || prompt.trocoOk));
  const trocoIsPrimary = isConcluir && prompt.needTroco && !prompt.need4dig;
  return (
    <Dialog open={!!prompt} onOpenChange={(o) => !lock && !o && setPrompt(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{prompt?.status === "concluida" ? "Confirmar entrega" : `Marcar como ${STATUS[prompt?.status] || ""}`}</DialogTitle>
        </DialogHeader>
        {prompt && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{prompt.label}</div>
            {isConcluir ? (
              <>
                {prompt.need4dig && (
                  <div className="space-y-2">
                    <Input type="tel" inputMode="numeric" autoFocus maxLength={4} value={prompt.value}
                      onChange={(e) => setPrompt({ ...prompt, value: e.target.value.replace(/\D/g, "").slice(0, 4) })} disabled={lock}
                      className="text-3xl h-16 text-center mono tracking-widest" data-testid="mobile-prompt-4dig" placeholder="0000" />
                    <div className="text-xs text-center text-muted-foreground">Pergunte ao cliente e digite os 4 últimos dígitos do celular dele</div>
                  </div>
                )}
                {!prompt.needTroco && prompt.valor > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-sm text-center" data-testid="mobile-pgto-info">
                    Pagamento: <b>{prompt.formaNome || "—"}</b> · <span className="mono font-bold">{fmtBRL(prompt.valor)}</span> <span className="text-slate-500">(sem troco)</span>
                  </div>
                )}
                <ObsField prompt={prompt} setPrompt={setPrompt} lock={lock} />
                {prompt.needTroco && <TrocoCard prompt={prompt} setPrompt={setPrompt} onConfirm={onConfirm} lock={lock} saving={saving} isSuccess={isSuccess} />}
                {!trocoIsPrimary && <ConfirmBtn onClick={() => onConfirm()} disabled={lock || !canConfirm} saving={saving} isSuccess={isSuccess} label="Confirmar entrega" testId="mobile-confirm-status" />}
              </>
            ) : (
              <>
                <Textarea autoFocus value={prompt.value} onChange={(e) => setPrompt({ ...prompt, value: e.target.value })} disabled={lock}
                  placeholder="Ex: cliente ausente" rows={4} data-testid="mobile-prompt-motivo" />
                <ConfirmBtn onClick={() => onConfirm()} disabled={lock} saving={saving} isSuccess={isSuccess} label="Confirmar" testId="mobile-confirm-status" />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
